import React, { useContext, useState } from "react";

import { AuthContext, useUserInfo } from "@swa-ui/authentication";
import { Dialog, Icon, Link, Shimmer, Text, Toggle } from "@swa-ui/core";
import i18n from "@swa-ui/locale";
import { PlacementFactory } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { CompanySelection } from "../CompanySelection";
import styles from "./SwaBizToggle.module.scss";

const DIALOG_STATE = {
  COMPANY_SELECTION: "COMPANY_SELECTION",
  LEARN_MORE: "LEARN_MORE",
  NO_ASSOCIATION: "NO_ASSOCIATION",
};

/**
 * SwaBizToggle allows toggling the SWABIZ account booking flow
 */
export const SwaBizToggle = () => {
  const { loadingTokenExchange, switchAuthType } = useContext(AuthContext);
  const { getCorporateInfo, userInfoLoading } = useUserInfo();
  const { activeCompanyIdAssociations, name: selectedCompanyName } = getCorporateInfo?.() || {};
  const [dialogState, setDialogState] = useState(null);
  const contentMap = {
    [DIALOG_STATE.COMPANY_SELECTION]: () => <CompanySelection {...getCompanySelectionProps()} />,
    [DIALOG_STATE.LEARN_MORE]: () => <PlacementFactory id="swabizLearnMore" />,
    [DIALOG_STATE.NO_ASSOCIATION]: () => <PlacementFactory id="swabizNoAssoc" />,
  };
  const loading = loadingTokenExchange || userInfoLoading;

  return (
    <div className={styles.container}>
      <div className={styles.label}>
        <Icon {...getIconProps()} />
        <div className={styles.labelContainer}>
          <Text className={styles.labelText}>{i18n("SwaBizToggle__TOGGLE_LABEL")}</Text>
          {loading ? <Shimmer height="0.75rem" /> : renderLearnMoreOrCompanyName()}
        </div>
      </div>
      <Toggle {...getToggleProps()} />
      <Dialog {...getDialogProps()}>{renderDialogContent()}</Dialog>
    </div>
  );

  function renderLearnMoreOrCompanyName() {
    return selectedCompanyName ? (
      <Text className={classNames(styles.companyName, styles.labelSubtitle)}>
        {selectedCompanyName}
      </Text>
    ) : (
      <Link
        onClick={() => setDialogState(DIALOG_STATE.LEARN_MORE)}
        className={styles.labelSubtitle}
      >
        {i18n("SwaBizToggle__LEARN_MORE_LINK")}
      </Link>
    );
  }

  function renderDialogContent() {
    return contentMap[dialogState]?.() || contentMap[DIALOG_STATE.NO_ASSOCIATION]();
  }

  function getCompanySelectionProps() {
    return {
      companies: activeCompanyIdAssociations,
      onSelect: handleSwitchAuthType,
    };
  }

  function getDialogProps() {
    return {
      "aria-label": i18n("SwaBizToggle__DIALOG_ARIA_LABEL"),
      buttonGroupProps: {
        "aria-label-next": i18n("SwaBizToggle__BUTTON_GROUP_ARIA_NEXT"),
        "aria-label-previous": i18n("SwaBizToggle__BUTTON_GROUP_ARIA_PREVIOUS"),
      },
      onClose: handleClose,
      revealed: dialogState !== null,
      showClose: true,
    };
  }

  function getIconProps() {
    return {
      className: styles.icon,
      color: "book-with-swabiz-icon",
      name: "Baggage",
      size: "size24",
    };
  }

  function getToggleProps() {
    return {
      "aria-label": i18n("SwaBizToggle__TOGGLE_ARIA_LABEL"),
      checked: !!selectedCompanyName,
      disabled: loading,
      onChange: handleToggleChange,
    };
  }

  function handleClose() {
    setDialogState(null);
  }

  async function handleSwitchAuthType(companyId) {
    setDialogState(null);
    await switchAuthType(companyId);
  }

  function handleToggleChange(checked) {
    if (checked) {
      const associationsLength = activeCompanyIdAssociations?.length;

      if (associationsLength === 1) {
        handleSwitchAuthType(activeCompanyIdAssociations[0].companyId);
      } else if (associationsLength > 1) {
        setDialogState(DIALOG_STATE.COMPANY_SELECTION);
      } else {
        setDialogState(DIALOG_STATE.NO_ASSOCIATION);
      }
    } else {
      handleSwitchAuthType();
    }
  }
};
